import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ComplianceConfigStep = ({ data, departmentInfo, onComplete, onBack, onCancel }) => {
  const [formData, setFormData] = useState({
    nasfundRate: data?.nasfundRate || '6.0',
    swtRate: data?.swtRate || '2.0',
    minimumWage: data?.minimumWage || '3.50',
    payrollFrequency: data?.payrollFrequency || 'fortnightly',
    overtimeMultiplier: data?.overtimeMultiplier || '1.5',
    publicHolidayMultiplier: data?.publicHolidayMultiplier || '2.0',
    leaveAccrualRate: data?.leaveAccrualRate || '1.67',
    complianceTemplate: data?.complianceTemplate || 'standard'
  });
  const [errors, setErrors] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const complianceTemplates = [
    {
      id: 'health',
      name: 'Health Department',
      description: 'Pre-configured for healthcare workers with shift allowances',
      settings: {
        nasfundRate: '6.0',
        swtRate: '2.0',
        minimumWage: '4.50',
        overtimeMultiplier: '1.5',
        publicHolidayMultiplier: '2.5',
        leaveAccrualRate: '2.0'
      }
    },
    {
      id: 'education',
      name: 'Education Department',
      description: 'Teacher-specific compliance with term-based leave',
      settings: {
        nasfundRate: '6.0',
        swtRate: '2.0',
        minimumWage: '4.00',
        overtimeMultiplier: '1.5',
        publicHolidayMultiplier: '2.0',
        leaveAccrualRate: '1.67'
      }
    },
    {
      id: 'finance',
      name: 'Finance Department',
      description: 'Financial sector compliance standards',
      settings: {
        nasfundRate: '6.0',
        swtRate: '2.0',
        minimumWage: '5.00',
        overtimeMultiplier: '1.5',
        publicHolidayMultiplier: '2.0',
        leaveAccrualRate: '1.67'
      }
    },
    {
      id: 'standard',
      name: 'Standard Government',
      description: 'Default PNG Employment Act 1978 compliance',
      settings: {
        nasfundRate: '6.0',
        swtRate: '2.0',
        minimumWage: '3.50',
        overtimeMultiplier: '1.5',
        publicHolidayMultiplier: '2.0',
        leaveAccrualRate: '1.67'
      }
    }
  ];

  const payrollFrequencyOptions = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'fortnightly', label: 'Fortnightly (Most Common)' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template?.id);
    setFormData(prev => ({
      ...prev,
      ...template?.settings,
      complianceTemplate: template?.id
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.nasfundRate || parseFloat(formData?.nasfundRate) < 0) {
      newErrors.nasfundRate = 'Valid NASFUND rate is required';
    }

    if (!formData?.swtRate || parseFloat(formData?.swtRate) < 0) {
      newErrors.swtRate = 'Valid SWT rate is required';
    }

    if (!formData?.minimumWage || parseFloat(formData?.minimumWage) < 0) {
      newErrors.minimumWage = 'Valid minimum wage is required';
    }

    if (!formData?.payrollFrequency) {
      newErrors.payrollFrequency = 'Payroll frequency is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onComplete?.(formData);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Compliance Configuration</h2>
        <p className="text-gray-600">
          Configure PNG Employment Act 1978 compliance settings for your department.
        </p>
      </div>

      {/* Quick-Start Templates */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Quick-Start Templates
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {complianceTemplates?.map((template) => (
            <button
              key={template?.id}
              onClick={() => handleTemplateSelect(template)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                selectedTemplate === template?.id
                  ? 'border-emerald-600 bg-emerald-50' :'border-gray-200 hover:border-emerald-300'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <p className="font-semibold text-gray-900">{template?.name}</p>
                {selectedTemplate === template?.id && (
                  <Icon name="CheckCircle" size={20} className="text-emerald-600" />
                )}
              </div>
              <p className="text-sm text-gray-600">{template?.description}</p>
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* NASFUND Rate */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <Icon name="Info" size={20} className="text-blue-600 mr-3 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900 mb-1">NASFUND Contribution</p>
              <p className="text-xs text-gray-600 mb-3">
                National Superannuation Fund - Standard rate is 6% (employer contribution)
              </p>
              <Input
                label="NASFUND Rate (%)"
                type="number"
                step="0.1"
                required
                value={formData?.nasfundRate}
                onChange={(e) => handleChange('nasfundRate', e?.target?.value)}
                error={errors?.nasfundRate}
              />
            </div>
          </div>
        </div>

        {/* SWT Rate */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <Icon name="Info" size={20} className="text-blue-600 mr-3 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900 mb-1">Salary and Wages Tax (SWT)</p>
              <p className="text-xs text-gray-600 mb-3">
                Standard SWT rate is 2% for government employees
              </p>
              <Input
                label="SWT Rate (%)"
                type="number"
                step="0.1"
                required
                value={formData?.swtRate}
                onChange={(e) => handleChange('swtRate', e?.target?.value)}
                error={errors?.swtRate}
              />
            </div>
          </div>
        </div>

        {/* Minimum Wage */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <Icon name="Info" size={20} className="text-blue-600 mr-3 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900 mb-1">Minimum Wage</p>
              <p className="text-xs text-gray-600 mb-3">
                PNG national minimum wage (PGK per hour)
              </p>
              <Input
                label="Minimum Wage (PGK/hour)"
                type="number"
                step="0.01"
                required
                value={formData?.minimumWage}
                onChange={(e) => handleChange('minimumWage', e?.target?.value)}
                error={errors?.minimumWage}
              />
            </div>
          </div>
        </div>

        {/* Payroll Frequency */}
        <Select
          label="Payroll Frequency"
          required
          options={payrollFrequencyOptions}
          value={formData?.payrollFrequency}
          onChange={(value) => handleChange('payrollFrequency', value)}
          error={errors?.payrollFrequency}
        />

        {/* Overtime and Leave Settings */}
        <div className="border-t pt-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Settings</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Overtime Multiplier"
              type="number"
              step="0.1"
              value={formData?.overtimeMultiplier}
              onChange={(e) => handleChange('overtimeMultiplier', e?.target?.value)}
              description="Standard: 1.5x"
            />

            <Input
              label="Public Holiday Multiplier"
              type="number"
              step="0.1"
              value={formData?.publicHolidayMultiplier}
              onChange={(e) => handleChange('publicHolidayMultiplier', e?.target?.value)}
              description="Standard: 2.0x"
            />

            <Input
              label="Leave Accrual Rate (days/month)"
              type="number"
              step="0.01"
              value={formData?.leaveAccrualRate}
              onChange={(e) => handleChange('leaveAccrualRate', e?.target?.value)}
              description="Standard: 1.67 days/month (20 days/year)"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between pt-6 border-t">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              iconName="ArrowLeft"
            >
              Back
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
            >
              Cancel
            </Button>
          </div>
          <Button
            type="submit"
            iconName="ArrowRight"
            iconPosition="right"
          >
            Continue to Team Access
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ComplianceConfigStep;